import React, { memo, useMemo, useCallback } from "react";
import LabelAndInputField from "./LabelAndInputField";
import CustomLabelDropdown from "./CustomLabelDropdown";
import "../css/formLayout.css";
import CustomUploadField from "./CustomUploadFile";
import CustomSearchDropdown from "./CustomSearchDropdown";
import CustomPhoneInput from "./CustomPhoneInput";
import { currencyTypeList, projectMilestoneStatusList } from "../../../../assets/common/lists";
import CustomButton from "../../../global/Button/CustomButton";
import "../../../../components/shared/trial/theme.css"

const Form = memo(
  ({
    fields,
    formData,
    onChange,
    onBlur,
    onClear,
    onSubmit,
    onCancel,
    documentType = "non-indexed",
    errors = {},
    extraComponent,
    extraButtons,
    showClearButton = true,
    showCancelButton = false,
    showSubmitButton = true,
    sectionConfig = {},
    isSubmitting = false,
    viewMode = false,
    milestoneConfig = {},
    onMilestoneDelete,
    setErrors,
  }) => {
    const normalizeValue = useCallback((value) => {
      // Native input event
      if (value?.target) {
        return value.target.value;
      }

      // AntD / custom select option object
      if (typeof value === "object" && value !== null) {
        if ("value" in value) return value.value;
      }

      return value;
    }, []);

    /**
     * Memoize sections to prevent recalculation on every render
     */
    const sections = useMemo(() => {
      return fields.reduce((acc, field) => {
        const section = field.section || "";
        if (!acc[section]) acc[section] = [];
        acc[section].push(field);
        return acc;
      }, {});
    }, [fields]);

    /**
     * Check if field should be rendered based on dependencies
     */
    const shouldRenderField = useCallback(
      (field) => {
        if (!field.dependsOn) return true;
        const dependentValue = formData[field.dependsOn];

        if (typeof field.showWhen === "function") {
          return field.showWhen(dependentValue, formData);
        }
        return Boolean(dependentValue);
      },
      [formData],
    );

    /**
     * Resolve section layout configuration
     */
    const resolveSectionLayout = useCallback(
      (sectionName) => {
        const config = sectionConfig[sectionName] || {};
        return {
          gridTemplateColumns: `repeat(${config.columns || 2}, 1fr)`,
          gap: config.gap || "16px",
          maxHeight: config.maxHeight,
        };
      },
      [sectionConfig],
    );

    /**
     * Convert base64 URL to file object for upload component
     */
    const convertBase64ToFile = useCallback(
      (base64Url, fieldName) => {
        if (!base64Url || typeof base64Url !== "string") return [];

        try {
          // Extract file extension from field configuration
          const field = fields.find((f) => f.name === fieldName);
          const acceptTypes = field?.accept || ".pdf";

          // Determine file type from base64 string
          let mimeType = "application/pdf";
          let extension = "pdf";

          if (
            base64Url.includes("image/jpeg") ||
            base64Url.includes("image/JPEG")
          ) {
            mimeType = "image/jpeg";
            extension = "jpg";
          } else if (base64Url.includes("image/png")) {
            mimeType = "image/png";
            extension = "png";
          } else if (base64Url.includes("image/jpg")) {
            mimeType = "image/jpg";
            extension = "jpg";
          }

          // Create a file-like object for the upload component
          return [
            {
              uid: `-${fieldName}`,
              name: `${fieldName}.${extension}`,
              status: "done",
              url: base64Url, // Store the complete base64 URL
              thumbUrl: mimeType.startsWith("image/") ? base64Url : null,
              type: mimeType,
              originFileObj: null, // Indicates this is from API, not user upload
            },
          ];
        } catch (error) {
          console.error(`Error converting base64 for ${fieldName}:`, error);
          return [];
        }
      },
      [fields],
    );

    /**
     * Get file list for document fields
     */
    const getFileList = useCallback(
      (field) => {
        const fieldValue = formData[field.name];

        // If it's already a file list array (from user upload), return as is
        if (Array.isArray(fieldValue) && fieldValue.length > 0) {
          return fieldValue;
        }

        // If it's a base64 URL string (from API), convert it
        if (typeof fieldValue === "string" && fieldValue.startsWith("data:")) {
          return convertBase64ToFile(fieldValue, field.name);
        }

        return [];
      },
      [formData, convertBase64ToFile],
    );

    /**
     * Handle file preview/download
     */
    const handlePreview = useCallback((file) => {
      if (!file.url) return;

      // For images, open in new tab
      if (file.type && file.type.startsWith("image/")) {
        const newWindow = window.open();
        newWindow.document.write(`
        <html>
          <head><title>Preview - ${file.name}</title></head>
          <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#000;">
            <img src="${file.url}" style="max-width:100%;max-height:100vh;" />
          </body>
        </html>
      `);
        return;
      }

      // For PDFs, open in new tab
      if (file.url.startsWith("data:application/pdf") || file.name.endsWith(".pdf")) {
        const newWindow = window.open();
        newWindow.document.write(`
    <html>
      <head><title>Preview - ${file.name}</title></head>
      <body style="margin:0;">
        <embed src="${file.url}" type="application/pdf" width="100%" height="100%" />
      </body>
    </html>
  `);
        return;
      }


      // For other files, trigger download
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, []);

    const isValidOption = useCallback((value, options) => {
      if (!value || !options || options.length === 0) return false;
      return options.some((opt) => opt.id === value || opt.value === value);
    }, []);

    /**
     * Render individual field based on type
     */

    const getFieldProperties = useCallback(
      (field) => {
        const properties = { ...field };

        // Handle dynamic label
        if (
          field.getDynamicLabel &&
          typeof field.getDynamicLabel === "function"
        ) {
          properties.label = field.getDynamicLabel(formData);
        }

        // Handle dynamic minDate
        if (
          field.getDynamicMinDate &&
          typeof field.getDynamicMinDate === "function"
        ) {
          properties.minDate = field.getDynamicMinDate(formData);
        }

        // Handle conditional disable based on dependsOn
        if (field.dependsOn && field.type === "select") {
          const dependentValue = formData[field.dependsOn];
          properties.isDisabled = !Boolean(dependentValue);
        }
        if (field.disabledWhen) {
          const dependentValue = formData[field.disabledWhen];
          properties.isDisabled = !Boolean(dependentValue);
        }

        return properties;
      },
      [formData],
    );

    const clearFieldError = useCallback((fieldName) => {
      if (setErrors) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    }, [setErrors]);

    const renderField = useCallback(
      (field) => {
        const fieldError = errors[field.name];
        const fieldProps = getFieldProperties(field);
        const fieldValue = formData[field.name];

        if (field.type === "select") {
          const effectiveValue = isValidOption(fieldValue, fieldProps.options)
            ? fieldValue
            : undefined;
          return (
            <CustomLabelDropdown
              key={field.key || field.name}
              labelText={fieldProps.label}
              isCompulsary={field.required}
              controlId={field.name}
              controlName={field.name}
              value={effectiveValue}
              options={fieldProps.options || []}
              placeholder={field.placeholder}
              isDisabled={field.isDisabled || viewMode}
              showSearch={field.showSearch}
              errorMessage={fieldError}
              handleSelectChange={(val) => {
                const normalized = normalizeValue(val);

                const nextFormData = {
                  ...formData,
                  [field.name]: normalized,
                };

                onChange(field.name, normalized);
              }}
              onBlur={() => onBlur && onBlur(field.name)}
            />
          );
        }
        if (field.type === "multi-select") {
          return (
            <CustomSearchDropdown
              key={field.name}
              labelText={field.label}
              isCompulsary={field.required}
              controlId={field.name}
              controlName={field.name}
              value={formData[field.name] || []}
              multiSelect={true}
              maxTagCount={field.maxTagCount || 2}
              options={field.options || []}
              placeholder={field.placeholder}
              isDisabled={field.isDisabled || viewMode}
              isReadOnly={field.isReadOnly || viewMode}
              showSearch={field.showSearch}
              errorMessage={fieldError}
              handleSelectChange={(e) => {
                const val = e.target.value;
                const normalized = Array.isArray(val)
                  ? val.map((v) => v?.value ?? v)
                  : [];

                onChange(field.name, normalized);
              }}
              onBlur={() => {
                if (onBlur) {
                  onBlur(field.name);
                }
              }}
            />
          );
        }

        if (field.type === "phone") {
          return (
            <CustomPhoneInput
              key={field.name}
              controlId={field.name}
              labelText={fieldProps.label}
              isCompulsary={field.required}
              value={formData[field.name] || ""}
              onChange={(val) => onChange(field.name, val)}
              onBlur={() => {
                if (onBlur) {
                  onBlur(field.name);
                }
              }}
              placeholder={field.placeholder}
              countryCode={field.countryCode || "+91"}
              errorMessage={fieldError}
              isReadOnly={field.isReadOnly}
              style={field.style}
            />
          );
        }

        if (field.type === "file") {
          const fileList = getFileList(field);

          return (
            <CustomUploadField
              key={field.name}
              controlId={field.name}
              labelText={fieldProps.label}
              isCompulsary={field.required}
              isRequired={field.required}
              accept={field.accept}
              multiple={field.multiple}
              disableUploader={field.disableUploader}
              maxCount={field.maxCount || 1}
              fileList={fileList}
              errorMessage={fieldError}
              disabled={viewMode}
              onPreview={handlePreview}
              handleFileChange={({ fileList: newFileList }) => {

                onChange(field.name, newFileList);
                if (newFileList && newFileList.length > 0) {
                  clearFieldError(field.name);
                }
                if (onBlur) {
                  onBlur(field.name);
                }
              }}
            />
          );
        }

        // if (field.type === "multiselect") {
        //   return (
        //     <CustomSearchDropdown
        //       key={field.name}
        //       labelText={field.label}
        //       isCompulsary={field.required}
        //       controlId={field.name}
        //       controlName={field.name}
        //       value={formData[field.name]}
        //       options={field.options || []}
        //       placeholder={field.placeholder}
        //       isDisabled={field.isDisabled || viewMode}
        //       multiSelect={true}
        //       maxTagCount={field.maxTagCount || 2}
        //       errorMessage={fieldError}
        //       handleSelectChange={(val) => {
        //         const normalized = Array.isArray(val)
        //           ? val.map(v => v?.value ?? v)
        //           : [];

        //         const nextFormData = {
        //           ...formData,
        //           [field.name]: normalized,
        //         };

        //         onChange(field.name, normalized);

        //         if (onBlur) {
        //           onBlur(field.name, normalized, nextFormData);
        //         }
        //       }}


        //     />
        //   );
        // }

        return (
          <LabelAndInputField
            key={field.name}
            controlId={field.name}
            controlName={field.name}
            labelText={fieldProps.label}
            isCompulsary={field.required}
            placeholder={field.placeholder}
            inputType={field.type || "text"}
            value={fieldValue || ""}
            handleInputChange={(e) => onChange(field.name, e.target.value)}
            handleBlur={() => {
              if (onBlur) {
                onBlur(field.name);
              }
            }}
            errorMessage={fieldError}
            isReadOnly={field.isReadOnly || viewMode}
            minDate={field.minDate}
            isRequired={field.required}
            maxLength={field.maxLength}
            pattern={field.pattern}
            suffixIcon={field.suffix}
          />
        );
      },
      [
        formData,
        errors,
        onChange,
        onBlur,
        viewMode,
        getFileList,
        handlePreview,
        getFieldProperties,
        isValidOption,
      ],
    );

    const sectionEntries = useMemo(() => {
      return Object.entries(sections)
        .map(([sectionName, sectionFields]) => ({
          sectionName,
          visibleFields: sectionFields.filter(shouldRenderField),
        }))
        .filter(({ visibleFields }) => visibleFields.length);
    }, [sections, shouldRenderField]);

    const handleFormSubmit = useCallback(
      (e) => {
        e.preventDefault();
        if (!isSubmitting && onSubmit) {
          onSubmit();
        }
      },
      [isSubmitting, onSubmit]
    );

    const handleFormClear = useCallback(() => {
      if (!isSubmitting && onClear) {
        onClear();
      }
    }, [isSubmitting, onClear]);

    const handleFormCancel = useCallback(() => {
      if (!isSubmitting && onCancel) {
        onCancel();
      }
    }, [isSubmitting, onCancel]);



    const shouldShowMilestones = useMemo(() => {
      if (!milestoneConfig.showWhen) return false;
      if (typeof milestoneConfig.showWhen === "function") {
        return milestoneConfig.showWhen(formData);
      }
      return Boolean(formData[milestoneConfig.showWhen]);
    }, [milestoneConfig, formData]);

    const milestones = useMemo(() => {
      return formData.milestones || [];
    }, [formData.milestones]);

    const milestoneFields = useMemo(
      () => [
        {
          name: "milestoneName",
          label: "Milestone Name",
          type: "text",
          required: true,
          placeholder: "Enter milestone name",
        },
        {
          name: "description",
          label: "Description",
          type: "text",
          required: false,
          placeholder: "Enter description",
        },
        {
          name: "amount",
          label: "Amount",
          type: "number",
          required: true,
          placeholder: "Enter amount",
        },
        {
          name: "currency",
          label: "Currency",
          type: "select",
          required: true,
          placeholder: "Select currency",
          options: currencyTypeList,
        },
        {
          name: "status",
          label: "Status",
          type: "select",
          required: true,
          placeholder: "Select status",
          options: projectMilestoneStatusList,
        },
        {
          name: "milestoneStartDate",
          label: "Start Date",
          type: "date",
          required: true,
        },
        {
          name: "milestoneEndDate",
          label: "End Date",
          type: "date",
          required: true,
        },
      ],
      [],
    );

    const handleAddMilestone = useCallback(() => {
      const newMilestone = {
        milestoneName: "",
        description: "",
        amount: "",
        currency: "",
        status: "",
        milestoneStartDate: "",
        milestoneEndDate: "",
      };
      onChange("milestones", [...milestones, newMilestone]);
    }, [milestones, onChange]);

    const handleDeleteMilestone = useCallback(
      (index) => {
        const milestoneToDelete = milestones[index];
        const updatedMilestones = milestones.filter((_, i) => i !== index);

        // If the milestone has an ID (existing milestone), notify parent about deletion
        if (milestoneToDelete?.id && onMilestoneDelete) {
          onMilestoneDelete(milestoneToDelete.id);
        }

        onChange("milestones", updatedMilestones);
      },
      [milestones, onChange, onMilestoneDelete],
    );

    const handleMilestoneChange = useCallback(
      (index, fieldName, value) => {
        const updatedMilestones = [...milestones];
        updatedMilestones[index] = {
          ...updatedMilestones[index],
          [fieldName]: value,
        };
        onChange("milestones", updatedMilestones);
      },
      [milestones, onChange],
    );

    const handleMilestoneBlur = useCallback(
      (index, fieldName) => {
        if (onBlur) {
          onBlur(`milestones[${index}].${fieldName}`);
        }
      },
      [onBlur],
    );

    const renderMilestoneField = useCallback(
      (milestone, index, fieldConfig) => {
        const fieldName = fieldConfig.name;
        const milestoneFieldKey = `milestones[${index}].${fieldName}`;

        // Get error from errors prop (passed from parent via useFormValidation)
        const fieldError = errors[milestoneFieldKey];

        const fieldValue = milestone[fieldName];
        const isReadOnly = milestoneConfig.readOnly || milestone.isReadOnly;


        if (fieldConfig.type === "select") {
          return (
            <CustomLabelDropdown
              key={`milestone-${index}-${fieldName}`}
              labelText={fieldConfig.label}
              isCompulsary={fieldConfig.required}
              controlId={`milestone-${index}-${fieldName}`}
              controlName={fieldName}
              value={fieldValue || ""}
              options={fieldConfig.options || []}
              placeholder={fieldConfig.placeholder}
              isDisabled={isReadOnly}
              errorMessage={fieldError}
              handleSelectChange={(val) => {
                if (!isReadOnly) {
                  handleMilestoneChange(index, fieldName, val);
                  handleMilestoneBlur(index, fieldName);
                }
              }}
            />
          );
        }

        return (
          <LabelAndInputField
            key={`milestone-${index}-${fieldName}`}
            controlId={`milestone-${index}-${fieldName}`}
            controlName={fieldName}
            labelText={fieldConfig.label}
            isCompulsary={fieldConfig.required}
            placeholder={fieldConfig.placeholder}
            inputType={fieldConfig.type || "text"}
            value={fieldValue || ""}
            handleInputChange={(e) => {
              if (!isReadOnly) {
                handleMilestoneChange(index, fieldName, e.target.value);
              }
            }}
            handleBlur={() => handleMilestoneBlur(index, fieldName)}
            errorMessage={fieldError}
            isReadOnly={isReadOnly}
          />
        );
      },
      [
        errors,
        milestoneConfig.readOnly,
        handleMilestoneChange,
        handleMilestoneBlur,
      ],
    );

    return (
      <form className="formfeild-container" onSubmit={handleFormSubmit}>
        <div className="form-body">
          {sectionEntries.map(({ sectionName, visibleFields }) => (
            <div key={sectionName} className="form-section">
              {!sectionConfig[sectionName]?.hideTitle && (
                <div className="form-section-title">{sectionName}</div>
              )}
              <div className="form-grid" style={resolveSectionLayout(sectionName)}>
                {visibleFields.map(renderField)}
              </div>
            </div>
          ))}
          {shouldShowMilestones && (
            <div className="form-section milestone-section">
              <div className="milestone-header">
                <div className="form-section-title">Milestones</div>
                {!milestoneConfig.readOnly && (
                  <CustomButton
                    variant="primary"
                    onClick={handleAddMilestone}
                    disabled={isSubmitting}
                  >
                    Add Milestone
                  </CustomButton>
                )}
              </div>

              {milestones.length === 0 ? (
                <div className="milestone-empty">
                  <p>No milestones added yet.</p>
                </div>
              ) : (
                <div className="milestone-list">
                  {milestones.map((milestone, index) => (
                    <div key={index} className="milestone-item">
                      <div className="milestone-item-header">
                        <span className="milestone-number">
                          Milestone {index + 1}
                        </span>
                        {!milestoneConfig.readOnly && !milestone.isReadOnly && (
                          <CustomButton
                            variant="outlined"
                            onClick={() => handleDeleteMilestone(index)}
                            disabled={isSubmitting}
                          >
                            Delete
                          </CustomButton>
                        )}
                      </div>
                      <div
                        className="form-grid form-gap"
                        style={resolveSectionLayout("milestones")}
                      >
                        {milestoneFields.map((fieldConfig) =>
                          renderMilestoneField(milestone, index, fieldConfig),
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {extraComponent}
        </div>

        {/* FIXED ACTIONS */}
        <div className="form-actions-button">
          {showSubmitButton && (
            <CustomButton
              className="btn-update"
              variant="primary"
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </CustomButton>
          )}
          {showClearButton && (
            <CustomButton
              variant="outlined"
              className="btn-submit"
              onClick={handleFormClear}
              disabled={isSubmitting}
            >
              Clear
            </CustomButton>
          )}
          {showCancelButton && (
            <CustomButton
              variant="outlined"
              className="btn-submit"
              onClick={handleFormCancel}
              disabled={isSubmitting}
            >
              Cancel
            </CustomButton>
          )}
          {extraButtons}
        </div>
      </form>
    );
  },
);

Form.displayName = "Form";

export default Form;
